export interface VisorgisSector {
  id: number;
  name: string;
}

interface SectorSource {
  sector_id?: number | string | null;
  sector_name?: string | null;
  SECTOR?: string | null;
  properties?: SectorSource;
}

function asSectorSource(source: unknown): SectorSource | null {
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return null;
  }

  return source as SectorSource;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
}

function normalizeNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return null;
    }

    const parsedValue = Number(trimmedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  return null;
}

function getSectorData(source: unknown): SectorSource | null {
  const record = asSectorSource(source);

  if (!record) {
    return null;
  }

  return record.properties ?? record;
}

function getSectorId(source: unknown): number | null {
  const sectorData = getSectorData(source);

  if (!sectorData) {
    return null;
  }

  return normalizeNumber(sectorData.sector_id);
}

function getSectorName(
  source: unknown,
  sectorsById: Map<number, string> = new Map()
): string | null {
  const sectorData = getSectorData(source);

  if (!sectorData) {
    return null;
  }

  const directSectorName =
    normalizeString(sectorData.sector_name) ??
    normalizeString(sectorData.SECTOR);

  if (directSectorName) {
    return directSectorName;
  }

  const sectorId = getSectorId(sectorData);

  if (sectorId == null) {
    return null;
  }

  return normalizeString(sectorsById.get(sectorId));
}

export function getSectorLabel(
  source: unknown,
  sectorsById: Map<number, string> = new Map()
): string {
  return getSectorName(source, sectorsById) ?? '-';
}

export function getSectorLabelFromCollection(
  sources: unknown[],
  sectorsById: Map<number, string> = new Map()
): string {
  const sectorNames = Array.from(
    new Set(
      sources
        .map(source => getSectorName(source, sectorsById))
        .filter((sectorName): sectorName is string => Boolean(sectorName))
    )
  );

  return sectorNames.length > 0 ? sectorNames.join(' / ') : '-';
}

export function matchesSelectedSectors(
  source: unknown,
  selectedSectorIds: number[],
  sectorsById: Map<number, string> = new Map()
): boolean {
  if (!selectedSectorIds.length) {
    return true;
  }

  const sectorId = getSectorId(source);

  if (sectorId != null && selectedSectorIds.includes(sectorId)) {
    return true;
  }

  const sectorName = getSectorName(source, sectorsById);

  if (!sectorName) {
    return false;
  }

  const selectedSectorNames = new Set(
    selectedSectorIds
      .map(id => normalizeString(sectorsById.get(id)))
      .filter((selectedName): selectedName is string => Boolean(selectedName))
  );

  return selectedSectorNames.has(sectorName);
}
